/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * VoiceSettings — Popover for TTS voice selection and speech rate.
 */

import React, { useState } from 'react';
import {
  IconButton,
  Popover,
  Box,
  Typography,
  Slider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  type SxProps,
  type Theme,
} from '@mui/material';
import { Settings as SettingsIcon } from '@mui/icons-material';

interface VoiceSettingsProps {
  voices: SpeechSynthesisVoice[];
  selectedVoice: SpeechSynthesisVoice | null;
  onVoiceChange: (voice: SpeechSynthesisVoice) => void;
  voiceRate: number;
  onRateChange: (rate: number) => void;
  disabled?: boolean;
  sx?: SxProps<Theme>;
}

export default function VoiceSettings({
  voices,
  selectedVoice,
  onVoiceChange,
  voiceRate,
  onRateChange,
  disabled,
  sx,
}: VoiceSettingsProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  if (voices.length === 0) return null;

  return (
    <>
      <Tooltip title="Voice settings" arrow>
        <IconButton
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          disabled={disabled}
          sx={sx}
          aria-label="Voice settings"
        >
          <SettingsIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Box sx={{ p: 2, width: 280 }}>
          <Typography variant="subtitle2" gutterBottom>
            Voice Settings
          </Typography>

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Voice</InputLabel>
            <Select
              value={selectedVoice?.name ?? ''}
              label="Voice"
              onChange={(e) => {
                const v = voices.find((voice) => voice.name === e.target.value);
                if (v) onVoiceChange(v);
              }}
            >
              {voices.map((v) => (
                <MenuItem key={v.name} value={v.name}>
                  {v.name} ({v.lang})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="body2" gutterBottom>
            Speed: {voiceRate.toFixed(1)}x
          </Typography>
          <Slider
            min={0.5}
            max={2}
            step={0.1}
            value={voiceRate}
            onChange={(_, val) => onRateChange(val as number)}
            valueLabelDisplay="auto"
            size="small"
          />
        </Box>
      </Popover>
    </>
  );
}
